/**
 * CreatePostSplitButton Component
 *
 * Shared split button for creating posts with multiple import options.
 * Used in Dashboard, Creator, and Calendar pages.
 *
 * Features:
 * - Primary action: Create post (left button)
 * - Dropdown menu: Canva Create, Canva Import, Bulk Import (right button)
 * - Optional extra menu items slot
 * - Automatic Canva account detection for import option
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { Icon } from './Icon';
import { RetryImage } from './RetryImage';
import { ICONS } from '@/constants/icons';
import { Tooltip } from './Tooltip';
import { useCanvaChannels } from '@/hooks/useCanvaImport';
import { useLockedEntry } from '@/hooks/useLockedEntry';
import { focusSurface } from './focusStyles';
import { LockBadge } from '@/components/gating/LockBadge';

interface CreatePostSplitButtonProps {
  /** Handler for the main "Create Post" button */
  onCreatePost: () => void;
  /** Handler for Bulk Import menu item */
  onBulkImport: () => void;
  /** Optional handler for Canva Import (only shown if provided) */
  onCanvaImport?: () => void;
  /** Button size */
  size?: 'sm' | 'md';
  /** Extra menu items to render before the standard ones */
  extraMenuItems?: React.ReactNode;
}

export const CreatePostSplitButton = ({
  onCreatePost,
  onBulkImport,
  onCanvaImport,
  size = 'md',
  extraMenuItems,
}: CreatePostSplitButtonProps) => {
  const { t } = useTranslation('common');
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const createMenuRef = useRef<HTMLDivElement>(null);
  // Canva is plan-gated: the entry stays visible but locked (click opens the upsell) when the plan lacks it.
  const canvaLock = useLockedEntry('canva');
  const canvaEnabled = canvaLock.decided && !canvaLock.locked;

  // Single Canva-scoped fetch (including inactive/revoked channels) so the
  // "Import from Canva" button stays visible whether the channel is active
  // or revoked — the reconnect prompt itself lives inside the import modal
  // (CanvaImportDialog / CanvaAccountSelector), not in this menu.
  // Not fetched while Canva is locked or undecided: a background read would only 403.
  const { data: canvaChannels } = useCanvaChannels({ includeInactive: true, enabled: canvaEnabled });
  const hasAnyCanva = (canvaChannels?.length ?? 0) > 0;

  // Close create menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (createMenuRef.current && !createMenuRef.current.contains(event.target as Node)) {
        setCreateMenuOpen(false);
      }
    };
    if (createMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [createMenuOpen]);

  const handleCreatePost = () => {
    onCreatePost();
    setCreateMenuOpen(false);
  };

  const handleBulkImport = () => {
    onBulkImport();
    setCreateMenuOpen(false);
  };

  const handleCanvaImport = () => {
    if (canvaLock.locked) {
      canvaLock.showUpsell();
      setCreateMenuOpen(false);
      return;
    }
    if (onCanvaImport) {
      onCanvaImport();
      setCreateMenuOpen(false);
    }
  };

  return (
    <div className="relative inline-flex gap-1.5 sm:gap-2" ref={createMenuRef}>
      {/* Left Button: Create Post */}
      <Button
        variant="primary"
        size={size}
        onClick={handleCreatePost}
        className="flex items-center gap-2 h-[clamp(32px,4vh,38px)]"
      >
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <span className="material-symbols-outlined leading-none" style={{ fontSize: 'clamp(14px, 2vh, 18px)' }}>edit_square</span>
        <span className="hidden sm:inline">{t('create_post')}</span>
      </Button>

      {/* Mobile-only: standalone Canva icon button (shown when a Canva channel exists, active or revoked) */}
      {canvaEnabled && hasAnyCanva && onCanvaImport && (
        <Tooltip content={t('import_from_canva')}>
          <button
            onClick={handleCanvaImport}
            className="lg:hidden flex items-center justify-center w-[clamp(32px,4vh,38px)] h-[clamp(32px,4vh,38px)] rounded-lg bg-primary-400 hover:bg-primary-600 shadow-md hover:shadow-lg transition-colors flex-shrink-0"
            aria-label={t('import_from_canva')}
          >
            <img
              src="/assets/img/canva-white.svg"
              alt=""
              className="w-6 h-6"
            />
          </button>
        </Tooltip>
      )}

      {/* Right Button: Dropdown Toggle */}
      <Button
        variant="primary"
        size={size}
        onClick={() => setCreateMenuOpen(!createMenuOpen)}
        className="flex !px-3 h-[clamp(32px,4vh,38px)]"
        aria-label={t('more_create_options')}
        aria-expanded={createMenuOpen}
      >
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <span className="material-symbols-outlined leading-none flex-shrink-0" style={{ fontSize: 'clamp(14px, 2vh, 18px)' }}>drag_indicator</span>
      </Button>

      {/* Dropdown Menu */}
      {createMenuOpen && (
        <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {/* Extra Menu Items Slot */}
          {extraMenuItems && (
            <>
              {extraMenuItems}
              <div className="border-b border-gray-200 my-1" />
            </>
          )}

          {/* Import from Canva - button stays visible whether the channel is active or
              revoked; the reconnect prompt itself renders inside the import modal. */}
          {(canvaLock.locked || (onCanvaImport && canvaEnabled && hasAnyCanva)) && (
            <button
              onClick={handleCanvaImport}
              className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-gray-700 hover:bg-gray-100 ${focusSurface}`}
              {...(canvaLock.locked
                ? {
                    'data-testid': canvaLock.testId,
                    'aria-label': canvaLock.ariaLabelFor(t('import_from_canva')),
                    title: canvaLock.label,
                  }
                : {})}
            >
              <RetryImage
                src="/assets/img/canva-circle.png"
                alt=""
                className="w-4 h-4"
              />
              {t('import_from_canva')}
              {canvaLock.locked && <LockBadge className="ml-auto" />}
            </button>
          )}

          {/* Bulk Import */}
          <button
            onClick={handleBulkImport}
            className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-gray-700 hover:bg-gray-100"
          >
            <Icon name={ICONS.UPLOAD} size="sm" ariaHidden />
            {t('bulk_import')}
          </button>
        </div>
      )}
    </div>
  );
};
